/** Admin — Survey responses + analytics. */
import React, { useEffect, useState } from 'react';
import { Loader2, Download, BarChart3, MessageSquare, Star, ThumbsUp, FileText, FileType } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { getToken } from '@/lib/adminApi';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LOVED_LABELS = {
  service_quality_comfort: 'მომსახურების ხარისხი',
  attentive_trusted_team:  'სანდო გუნდი',
  smart_dispenser:         'ჭკვიანი დისპენსერი',
  prices:                  'ფასები',
  product_variety:         'პროდუქტების მრავალფეროვნება',
};

async function callApi(path) {
  const t = getToken();
  const r = await fetch(`${API}${path}`, {
    headers: { Authorization: t ? `Bearer ${t}` : '' },
  });
  if (!r.ok) throw new Error((await r.json()).detail || `${r.status}`);
  return r.json();
}

function StarsRow({ avg }) {
  const full = Math.round(avg);
  return (
    <div className="flex items-center gap-1.5 mt-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`h-5 w-5 ${n <= full ? 'fill-[#F25C05] text-[#F25C05]' : 'text-[#0A4D8C26]'}`} strokeWidth={1.5} />
      ))}
      <span className="ml-2 font-display font-extrabold text-2xl text-[#05223D] tabular-nums">{avg.toFixed(1)}</span>
      <span className="text-xs text-[#465B70]">/ 5</span>
    </div>
  );
}

function Bar({ label, count, max }) {
  const pct = max ? Math.round((count / max) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-[#05223D] font-bold truncate">{label}</span>
        <span className="text-[#465B70] tabular-nums shrink-0 ml-2">{count}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-[#F5F2EB] overflow-hidden">
        <div className="h-full rounded-full bg-[#F25C05]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function downloadCsv(rows) {
  if (!rows.length) return;
  const headers = [
    'created_at', 'name', 'phone',
    'overall_rating', 'overall_comment',
    'delivery_rating', 'loved_most',
    'improvements', 'future_wishes', 'recommend_score',
  ];
  const csv = [
    headers.join(','),
    ...rows.map((r) =>
      headers.map((h) => {
        let v = r[h];
        if (Array.isArray(v)) v = v.join('|');
        if (v == null) v = '';
        v = String(v).replace(/"/g, '""');
        return `"${v}"`;
      }).join(','),
    ),
  ].join('\n');
  const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `smartpaw-survey-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminSurveys() {
  const [analytics, setAnalytics] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(''); // '' | 'pdf' | 'docx'
  const [exportError, setExportError] = useState('');

  async function downloadReport(format) {
    setExportError('');
    setExporting(format);
    try {
      const t = getToken();
      const res = await fetch(`${API}/api/admin/survey/export/${format}`, {
        headers: { Authorization: t ? `Bearer ${t}` : '' },
      });
      if (!res.ok) {
        let msg = `${res.status}`;
        try {
          const j = await res.json();
          msg = j.detail || msg;
        } catch {
          /* not JSON */
        }
        throw new Error(msg);
      }
      // Prefer filename from Content-Disposition; fall back to a sensible default.
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^";]+)"?/i);
      const fname = match
        ? match[1]
        : `smartpaw-survey-report-${new Date().toISOString().slice(0, 10)}.${format}`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(`${format.toUpperCase()} export failed: ${e.message}`);
    } finally {
      setExporting('');
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const [a, r] = await Promise.all([
          callApi('/api/admin/survey/analytics'),
          callApi('/api/admin/survey/responses'),
        ]);
        setAnalytics(a);
        setRows(r);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="py-12 text-center text-[#465B70]"><Loader2 className="inline h-5 w-5 animate-spin mr-2" />Loading…</div>;
  if (error) return <div className="text-[#9b1c1c] p-4 rounded-xl border border-[#9b1c1c]/30 bg-[#9b1c1c]/8">{error}</div>;
  if (!analytics) return null;

  const maxLoved = Math.max(1, ...Object.values(analytics.loved_most));
  const nps = analytics.nps;

  return (
    <div className="space-y-6" data-testid="admin-surveys-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#05223D] flex items-center gap-2.5">
            <BarChart3 className="h-6 w-6 text-[#F25C05]" /> Customer Surveys
          </h1>
          <p className="text-sm text-[#465B70] mt-1">
            {analytics.total} {analytics.total === 1 ? 'response' : 'responses'} received.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={() => downloadCsv(rows)} disabled={!rows.length} variant="outline" className="gap-2" data-testid="surveys-export-csv">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
        <Button
          onClick={() => downloadReport('pdf')}
          disabled={!rows.length || !!exporting}
          variant="outline"
          className="gap-2 border-[#F25C05]/40 text-[#F25C05] hover:bg-[#F25C05]/10 hover:text-[#F25C05]"
          data-testid="surveys-export-pdf"
        >
          {exporting === 'pdf' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileText className="h-4 w-4" />
          )}
          Export PDF
        </Button>
        <Button
          onClick={() => downloadReport('docx')}
          disabled={!rows.length || !!exporting}
          className="gap-2 bg-[#0A4D8C] hover:bg-[#053B70] text-white"
          data-testid="surveys-export-docx"
        >
          {exporting === 'docx' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileType className="h-4 w-4" />
          )}
          Export Word
        </Button>
        </div>
      </div>

      {exportError && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm" data-testid="surveys-export-error">
          {exportError}
        </div>
      )}

      <Tabs defaultValue="analytics" className="space-y-4">
        <TabsList>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="responses" data-testid="tab-responses">Individual responses ({rows.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="space-y-4">
          {/* Top KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#465B70]">Overall satisfaction</p>
                <StarsRow avg={analytics.overall.average} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#465B70]">Delivery rating</p>
                <StarsRow avg={analytics.delivery.average} />
              </CardContent>
            </Card>
            <Card className="border-[#F25C05]/30 bg-[#FFF6EE]">
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#F25C05]">NPS Score</p>
                <p className="font-display font-extrabold text-3xl text-[#05223D] mt-1 tabular-nums">{nps.score}</p>
                <p className="text-xs text-[#465B70] mt-1">
                  <span className="text-emerald-700 font-bold">{nps.promoters}</span> promoters ·
                  &nbsp;<span className="font-bold">{nps.passives}</span> passives ·
                  &nbsp;<span className="text-[#9b1c1c] font-bold">{nps.detractors}</span> detractors
                </p>
              </CardContent>
            </Card>
          </div>

          {/* What people love */}
          <Card>
            <CardContent className="p-5">
              <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#465B70] mb-4 flex items-center gap-2">
                <ThumbsUp className="h-3.5 w-3.5" /> What customers love most
              </p>
              {Object.entries(analytics.loved_most).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <Bar key={k} label={LOVED_LABELS[k] || k} count={v} max={maxLoved} />
              ))}
            </CardContent>
          </Card>

          {/* Distribution */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#465B70] mb-3">Overall rating distribution</p>
                {[5, 4, 3, 2, 1].map((s) => {
                  const c = analytics.overall.distribution[String(s)] || 0;
                  const max = Math.max(1, ...Object.values(analytics.overall.distribution));
                  return <Bar key={s} label={`${s} ★`} count={c} max={max} />;
                })}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#465B70] mb-3">Delivery rating distribution</p>
                {[5, 4, 3, 2, 1].map((s) => {
                  const c = analytics.delivery.distribution[String(s)] || 0;
                  const max = Math.max(1, ...Object.values(analytics.delivery.distribution));
                  return <Bar key={s} label={`${s} ★`} count={c} max={max} />;
                })}
              </CardContent>
            </Card>
          </div>

          {/* Free-text excerpts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#465B70] mb-3 flex items-center gap-2">
                  <MessageSquare className="h-3.5 w-3.5" /> Improvement suggestions ({analytics.recent_text.improvements.length})
                </p>
                <ul className="space-y-2 text-sm text-[#05223D] max-h-72 overflow-y-auto">
                  {analytics.recent_text.improvements.length === 0 ? (
                    <li className="text-[#465B70] italic">No responses yet.</li>
                  ) : analytics.recent_text.improvements.map((t, i) => (
                    <li key={i} className="border-l-2 border-[#F25C05]/30 pl-3 py-1">{t}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#465B70] mb-3 flex items-center gap-2">
                  <MessageSquare className="h-3.5 w-3.5" /> Future wishes ({analytics.recent_text.future_wishes.length})
                </p>
                <ul className="space-y-2 text-sm text-[#05223D] max-h-72 overflow-y-auto">
                  {analytics.recent_text.future_wishes.length === 0 ? (
                    <li className="text-[#465B70] italic">No responses yet.</li>
                  ) : analytics.recent_text.future_wishes.map((t, i) => (
                    <li key={i} className="border-l-2 border-[#0A4D8C]/30 pl-3 py-1">{t}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="responses">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="surveys-table">
                <thead className="bg-[#F5F2EB] text-left text-xs uppercase tracking-wide text-[#465B70]">
                  <tr>
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Phone</th>
                    <th className="px-4 py-3">Overall</th>
                    <th className="px-4 py-3">Delivery</th>
                    <th className="px-4 py-3">NPS</th>
                    <th className="px-4 py-3 min-w-[200px]">Loved</th>
                    <th className="px-4 py-3 min-w-[260px]">Improvements</th>
                    <th className="px-4 py-3 min-w-[260px]">Future wishes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-[#465B70]">No responses yet.</td></tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-[#0A4D8C12] hover:bg-[#FDFBF7]">
                      <td className="px-4 py-3 text-[#465B70] whitespace-nowrap">
                        {r.created_at ? new Date(r.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                      </td>
                      <td className="px-4 py-3 font-bold text-[#05223D]">{r.name || '—'}</td>
                      <td className="px-4 py-3 text-[#465B70]">{r.phone || '—'}</td>
                      <td className="px-4 py-3 tabular-nums"><Badge className="bg-[#F25C05]/15 text-[#F25C05] border border-[#F25C05]/30">{r.overall_rating} ★</Badge></td>
                      <td className="px-4 py-3 tabular-nums"><Badge className="bg-[#0A4D8C]/15 text-[#0A4D8C] border border-[#0A4D8C]/30">{r.delivery_rating} ★</Badge></td>
                      <td className="px-4 py-3 tabular-nums font-bold">{r.recommend_score}</td>
                      <td className="px-4 py-3 text-xs">
                        {(r.loved_most || []).map((k) => LOVED_LABELS[k] || k).join(', ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#465B70]">{r.improvements || '—'}</td>
                      <td className="px-4 py-3 text-xs text-[#465B70]">{r.future_wishes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
